import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { useGetCurrentUserQuery } from '../../store/services/api';
import { 
  LayoutGrid, 
  CalendarDays, 
  Settings, 
  Menu, 
  X, 
  PlusCircle,
  CreditCard,
  User,
  Shield,
  LogOut,
  Loader2,
  HelpCircle
} from 'lucide-react';
import { toast } from 'react-toastify';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((state: RootState) => state.auth);
  const { data: user, isLoading } = useGetCurrentUserQuery(undefined, {
    skip: !token
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Sayfa yükleniyorsa loading göster
  if (isLoading) {
    return (
      <aside className="fixed lg:sticky top-0 inset-y-0 left-0 z-40 w-64 bg-white h-screen border-r border-gray-100 flex flex-col items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#FF4444] animate-spin" />
        <p className="text-sm text-gray-500 mt-2">Yükleniyor...</p>
      </aside>
    );
  }

  // Kullanıcı yoksa sidebar'ı gösterme
  if (!user) {
    return (
      <aside className="fixed lg:sticky top-0 inset-y-0 left-0 z-40 w-64 bg-white h-screen border-r border-gray-100 flex flex-col items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#FF4444] animate-spin" />
        <p className="text-sm text-gray-500 mt-2">Kullanıcı bilgileri yükleniyor...</p>
      </aside>
    );
  }

  const handleLogout = () => {
    try {
      // First dispatch logout to clear all states and caches
      dispatch(logout());
      
      // Then show success message
      toast.success('Logged out successfully');
      
      // Finally navigate to login page
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error('An error occurred while logging out');
    }
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={toggleMobileMenu}
        className="lg:hidden fixed bottom-4 right-4 z-50 p-2 rounded-md bg-white border border-gray-100 shadow-lg"
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? (
          <X className="w-5 h-5 text-gray-600" />
        ) : (
          <Menu className="w-5 h-5 text-gray-600" />
        )}
      </button>

      {/* Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:sticky top-0 inset-y-0 left-0 z-40 w-64 bg-white h-screen border-r border-gray-100 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2 p-2">
            <img 
              src="/images/logo.svg" 
              alt="OurPhotos Logo" 
              className="w-40 h-10"
            />
            <span className="font-medium text-gray-900 sr-only">OurPhotos</span>
          </Link>
        </div>

        {/* Navigation */}
        <div className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          <Link
            to="/dashboard"
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
              isActive('/dashboard')
                ? 'bg-[#FF4444]/5 text-[#FF4444] font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <LayoutGrid className="w-4 h-4" />
            <span>Overview</span>
          </Link>

          {/* Events Header */}
          <div className="px-3 py-2 text-xs font-medium text-gray-500 mt-4">
            Events
          </div>
          
          {/* Events Section */}
          <Link
            to="/dashboard/events"
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
              isActive('/dashboard/events')
                ? 'bg-[#FF4444]/5 text-[#FF4444] font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <CalendarDays className="w-4 h-4" />
            <span>All Events</span>
          </Link>
          
          <Link
            to="/dashboard/events/new"
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
              isActive('/dashboard/events/new')
                ? 'bg-[#FF4444]/5 text-[#FF4444] font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <PlusCircle className="w-4 h-4" />
            <span>Create New</span>
          </Link>

          <Link
            to="/dashboard/how-to-use"
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
              isActive('/dashboard/how-to-use')
                ? 'bg-[#FF4444]/5 text-[#FF4444] font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <HelpCircle className="w-4 h-4" />
            <span>How to Use</span>
          </Link>

          {/* Settings Header */}
          <div className="px-3 py-2 text-xs font-medium text-gray-500 mt-4">
            Settings
          </div>
          
          {/* Settings Section */}
          <Link
            to="/dashboard/settings/profile"
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
              isActive('/dashboard/settings/profile')
                ? 'bg-[#FF4444]/5 text-[#FF4444] font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <User className="w-4 h-4" />
            <span>Profile</span>
          </Link>
          
          <Link
            to="/dashboard/settings/billing"
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
              isActive('/dashboard/settings/billing')
                ? 'bg-[#FF4444]/5 text-[#FF4444] font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <CreditCard className="w-4 h-4" />
            <span>Billing</span>
          </Link>
          
          <Link
            to="/dashboard/settings/security"
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
              isActive('/dashboard/settings/security')
                ? 'bg-[#FF4444]/5 text-[#FF4444] font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Shield className="w-4 h-4" />
            <span>Security</span>
          </Link>
        </div>
        
        {/* User Profile and Logout */}
        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-[#FF4444] flex items-center justify-center text-white">
              {user.full_name?.charAt(0).toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user.full_name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user.email}
              </p>
            </div>
          </div>
          
          <button 
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors text-gray-600 hover:bg-gray-50"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
            <span>Log out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;